import { Trans, useLingui } from "@lingui/react/macro";
import { Popover } from "@/components/Popover/Popover";
import { FilledButton } from "@medoma/components/Button/Button";
import CalendarIcon from "@medoma/components/icons/CalendarIcon";
import { useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./SelectDatePopover.module.scss";
import InputField from "@medoma/components/InputField/InputField";
import { machineFormat } from "@medoma/models/date-and-time";

type ISelectDateFormFields = {
  date: string;
};

export const SelectDatePopover = ({
  onDateSelect,
  selectedDate,
  trigger = "icon-and-text",
  disabled = false,
}: {
  onDateSelect: (date: Date) => void;
  selectedDate?: Date;
  trigger?: "icon" | "icon-and-text";
  disabled?: boolean;
}) => {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ISelectDateFormFields>({
    defaultValues: {
      date: selectedDate ? machineFormat(selectedDate, "yyyy-MM-dd") : "",
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (!newOpen) {
      // Reset form when popover is closed
      reset();
    }
  };

  const triggerConfig =
    trigger === "icon-and-text"
      ? { icon: <CalendarIcon />, text: t`Välj datum` }
      : { icon: <CalendarIcon />, ariaLabel: t`Välj datum` };

  return (
    <Popover
      trigger={{
        ...triggerConfig,
        disabled,
        weight: "regular",
      }}
      controlled={{ isOpen, onOpenChange: handleOpenChange }}
    >
      <form
        className={styles.content}
        onSubmit={handleSubmit((formValues) => {
          const date = new Date(formValues.date);
          onDateSelect(date);
          setIsOpen(false);
        })}
      >
        <InputField
          label={t`Välj datum`}
          showOptionalLabel={false}
          type="date"
          errorMessage={errors.date?.message}
          {...register("date", {
            required: {
              value: true,
              message: t`Välj ett datum`,
            },
          })}
        />
        <FilledButton type="submit" width="fill-container">
          <Trans>Gå till datum</Trans>
        </FilledButton>
      </form>
    </Popover>
  );
};
